import {
  Address,
  AMOUNT_ZERO,
  MarketAssetMeta,
  makeUsd,
  PERCENT_ZERO,
} from "@silo/types";
import { Effect } from "effect";
import { getTokenLabels } from "../utils/get-token-labels";
import { RawDashboardMarketAsset } from "./types";
import {
  getConfigDepositMode,
  getConfigIsDebtAprFixed,
  getConfigPointsByType,
} from "@silo/config";
import { convertToUsd, getIsDisabledCollateral } from "@silo/utils";
import { getDashboardAssetState } from "./get-dashboard-asset-state";
import { getDashboardAssetUserState } from "./get-dashboard-asset-user-state";

export type GetRawDashboardMarketAsset = ReturnType<
  typeof getRawDashboardMarketAsset
>;
export type GetRawDashboardMarketAssetSuccess =
  Effect.Effect.Success<GetRawDashboardMarketAsset>;
export type GetRawDashboardMarketAssetError =
  Effect.Effect.Error<GetRawDashboardMarketAsset>;
export type GetRawDashboardMarketAssetContext =
  Effect.Effect.Context<GetRawDashboardMarketAsset>;

interface GetRawDashboardMarketAssetParams {
  marketAssetMeta: MarketAssetMeta;
  user: Address;
}

export function getRawDashboardMarketAsset({
  marketAssetMeta,
  user,
}: GetRawDashboardMarketAssetParams) {
  return Effect.gen(function* () {
    const {
      protocolKey,
      tokenAddress,
      assetKey,
      chainKey,
      decimals,
      marketAddress,
      maxLtv,
      lt,
      protectedShareToken,
    } = marketAssetMeta;

    const [
      { priceNative, priceUsd, collateralBaseApr, debtBaseApr },
      { collateralBalance, protectedBalance, debtBalance },
    ] = yield* Effect.all(
      [
        getDashboardAssetState({ marketAddress, tokenAddress }),
        getDashboardAssetUserState({
          user,
          marketAddress,
          tokenAddress,
          protectedShareToken,
        }),
      ],
      { concurrency: "unbounded" }
    );

    const { symbol, name } = yield* getTokenLabels(marketAssetMeta);

    const { collateralPoints, protectedPoints, debtPoints } =
      getConfigPointsByType({
        protocolKey,
        marketAddress,
        assetKey,
      });

    const isDebtAprFixed = getConfigIsDebtAprFixed({
      protocolKey,
      marketAddress,
      assetKey,
    });

    const isDisabledCollateral = getIsDisabledCollateral(maxLtv);

    const collateralBalanceUsd = convertToUsd(
      collateralBalance,
      decimals,
      priceUsd
    );

    const protectedBalanceUsd = convertToUsd(
      protectedBalance,
      decimals,
      priceUsd
    );

    const debtBalanceUsd = convertToUsd(debtBalance, decimals, priceUsd);

    const depositBalanceUsd = makeUsd(collateralBalanceUsd + protectedBalanceUsd);

    const depositMode = getConfigDepositMode({
      protocolKey,
      marketAddress,
      assetKey,
    });

    const dashboardMarketAsset: RawDashboardMarketAsset = {
      assetKey,
      protocolKey,
      maxLtv,
      lt,

      priceNative,
      priceUsd,

      collateralTotal: AMOUNT_ZERO,
      collateralBaseApr,
      collateralRewardsApr: PERCENT_ZERO,
      collateralPoints,

      protectedTotal: AMOUNT_ZERO,
      protectedRewardsApr: PERCENT_ZERO,
      protectedPoints,

      debtTotal: AMOUNT_ZERO,
      debtBaseApr,
      debtRewardsApr: PERCENT_ZERO,
      debtPoints,

      isDebtAprFixed,
      isDisabledCollateral,
      depositMode,

      tokenAddress,
      chainKey,
      symbol,
      name,
      decimals,

      collateralBalance,
      collateralBalanceUsd,
      protectedBalance,
      protectedBalanceUsd,
      depositBalanceUsd,
      debtBalance,
      debtBalanceUsd,
    };

    return dashboardMarketAsset;
  });
}
